import React, { lazy, Suspense } from "react";
import { ArrowUpRight, PlayCircle, Sparkles } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import TrustedLogos from "../TrustedLogos";

/* Three.js canvas is the heaviest asset on the site (~225KB gzipped) —
 * lazy-loaded, but it's always needed above-the-fold on this page, so
 * webpackPreload tells webpack to inject a <link rel="preload"> for this
 * chunk immediately, fetching it in parallel with main.js instead of only
 * starting after main.js finishes executing and triggers the dynamic
 * import(). (webpackPrefetch would be wrong here — that's for chunks that
 * might be needed on a future navigation, not this one.) */
const Hero3D = lazy(() => import(/* webpackPreload: true */ "../3d/Hero3D"));

export default function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 1000], [0, 200]);
  const yCards = useTransform(scrollY, [0, 1000], [0, -100]);

  const containerVars = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.15, delayChildren: 0.3 }
    }
  };

  const lineVars = {
    hidden: { y: "120%" },
    show: { y: "0%", transition: { duration: 0.8, ease: [0.33, 1, 0.68, 1] } }
  };

  return (
    <section id="top" className="relative isolate min-h-[100vh] overflow-hidden" data-testid="hero-section">
      {/* 3D Canvas Background */}
      <Suspense fallback={null}>
        <Hero3D />
      </Suspense>

      {/* Cosmic background + aurora glow with Parallax */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10 bg-[#0A0F2C] starfield-bg">
        <div className="aurora-bg" />
      </motion.div>

      {/* Soft vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(10,15,44,0.6) 80%)" }} />

      {/* Content overlay */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-24 grid lg:grid-cols-12 gap-12 items-center pointer-events-none">
        <div className="lg:col-span-7 pointer-events-auto">
          <span className="eyebrow-tag-dark mb-6 inline-flex"><Sparkles className="w-3.5 h-3.5" /> TRUSTED AI REVENUE TRANSFORMATION PARTNER</span>
          
          {/* Masked Line-by-Line Reveal for Headline */}
          <motion.h1 
            className="display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-white mt-5 flex flex-col gap-2"
            variants={containerVars}
            initial="hidden"
            animate="show"
          >
            <span className="overflow-hidden block"><motion.span variants={lineVars} className="block">Turn AI Into Your</motion.span></span>
            <span className="overflow-hidden block"><motion.span variants={lineVars} className="block"><span className="text-brand-gradient animate-gradient-x">Next Revenue Engine.</span></motion.span></span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-6 text-2xl font-semibold text-white"
          >
            Autonomous AI That Generates, Engages &amp; Grows Revenue.
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-4 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
          >
            Venpa AI helps ambitious businesses accelerate growth with enterprise-grade <strong className="text-white">AI Sales Agents</strong>, <strong className="text-white">Customer Engagement</strong>, <strong className="text-white">Revenue Intelligence</strong>, and <strong className="text-white">Workflow Automation</strong>—built to increase productivity, improve customer experience, and drive measurable business outcomes.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#services" className="btn-brand group" data-testid="hero-cta-explore">
              Explore Solutions <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a href="#roi" className="btn-glass-dark" data-testid="hero-cta-roi">
              <PlayCircle className="w-5 h-5" /> Open ROI Calculator
            </a>
          </motion.div>
        </div>

        {/* RIGHT — Glass stat cards stacked, with parallax float */}
        <motion.div style={{ y: yCards }} className="lg:col-span-5 pointer-events-none">
          <div className="space-y-4 lg:ml-auto lg:max-w-sm">
            <StatGlass testid="hero-stat-latency" label="Voice latency" value="< 1.2s" sub="Conversational turn-taking" color="#FF8A6B" />
            <StatGlass testid="hero-stat-live" label="Live agents" value="214" sub="Running across client tenants" color="#4F7DF2" pulse />
            <StatGlass testid="hero-stat-retention" label="Client retention" value="97%" sub="Year-over-year" color="#C57DFF" />
          </div>
        </motion.div>
      </div>

      {/* Trusted logos strip at bottom of hero */}
      <div className="relative z-10 pb-12 px-6 md:px-12 max-w-7xl mx-auto">
        <p className="mono text-[10px] uppercase tracking-[0.25em] text-white/45 mb-5 text-center">Trusted by leading enterprises across India &amp; beyond</p>
        <TrustedLogos tone="dark" />
      </div>
    </section>
  );
}

function StatGlass({ label, value, sub, color, pulse, testid }) {
  return (
    <div className="glass-dark gradient-border rounded-glass-lg p-5 md:p-6 animate-float-medium pointer-events-auto" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="mono text-[10px] uppercase tracking-widest text-white/55">{label}</p>
        {pulse && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
      </div>
      <p className="display text-4xl md:text-5xl text-white mt-2" style={{ textShadow: `0 0 30px ${color}55` }}>{value}</p>
      <p className="text-xs text-white/55 mt-1">{sub}</p>
    </div>
  );
}
